/**
 * Tiện ích tối ưu hiệu suất
 */

// Hàm throttle để giới hạn tần suất gọi hàm
export const throttle = (func, limit) => {
  let lastCall = 0;
  return function(...args) {
    const now = Date.now();
    if (now - lastCall < limit) return;
    lastCall = now;
    return func.apply(this, args);
  }
};

// Hàm debounce để trì hoãn gọi hàm cho đến khi không còn event trong một khoảng thời gian
export const debounce = (func, wait) => {
  let timeout;
  return function(...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), wait);
  }
};

/**
 * Tiện ích tối ưu hiệu suất cho ứng dụng
 */

// Tối ưu Intersection Observer
export const createOptimizedObserver = (callback, options = {}) => {
  return new IntersectionObserver((entries, observer) => {
    // Sử dụng requestAnimationFrame để tối ưu rendering
    requestAnimationFrame(() => {
      callback(entries, observer);
    });
  }, {
    threshold: 0.1,
    rootMargin: '0px',
    ...options
  });
};

// Tối ưu việc sử dụng scroll event
export const optimizeScroll = (elementRef, callback, debounceTime = 10) => {
  let timeoutId;
  let lastScrollY = window.scrollY;
  let ticking = false;
  
  const handleScroll = () => {
    const currentScrollY = window.scrollY;
    
    if (!ticking) {
      // Sử dụng requestAnimationFrame để tối ưu rendering
      window.requestAnimationFrame(() => {
        callback(currentScrollY, lastScrollY);
        lastScrollY = currentScrollY;
        ticking = false;
      });
      
      ticking = true;
    }
  };
  
  window.addEventListener('scroll', handleScroll, { passive: true });
  
  // Hàm cleanup
  return () => {
    window.removeEventListener('scroll', handleScroll);
    if (timeoutId) clearTimeout(timeoutId);
  };
};

// Kiểm tra khả năng sử dụng hardware acceleration
export const checkHardwareAcceleration = () => {
  // Kiểm tra xem trình duyệt có hỗ trợ hardware acceleration không
  const canvas = document.createElement('canvas');
  const gl = canvas.getContext('webgl') || 
             canvas.getContext('experimental-webgl');
  
  if (!gl) {
    console.warn('WebGL not supported. Hardware acceleration may be limited');
    return false;
  }
  
  return true;
};

// Tắt animations nếu thiết bị yếu
export const disableAnimationsIfLowPower = () => {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  
  if (prefersReducedMotion) {
    document.body.classList.add('reduce-motion');
    return true;
  }
  
  // Kiểm tra xem thiết bị có phải là thiết bị yếu không
  const isLowPowerDevice = () => {
    // Kiểm tra số lượng CPU cores (gần đúng)
    const cpuCores = navigator.hardwareConcurrency || 1;
    
    // Kiểm tra thiết bị di động
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    // Nếu là thiết bị di động với ít hơn 4 cores, coi là thiết bị yếu
    return isMobile && cpuCores < 4;
  };
  
  if (isLowPowerDevice()) {
    document.body.classList.add('reduce-motion');
    return true;
  }
  
  return false;
};

// Khởi tạo tất cả các tối ưu hiệu suất
export const initPerformanceOptimizations = () => {
  // Ngăn reflow không cần thiết
  document.addEventListener('DOMContentLoaded', () => {
    // Đánh dấu các phần tử sẽ thay đổi để tối ưu reflow/repaint
    document.querySelectorAll('.glassmorphism-nav, .nav-link, .glass-button')
      .forEach(el => {
        el.style.willChange = 'opacity, transform';
      });
    
    console.log('Performance optimizations initialized');
  });
};
